import React from 'react'
import { Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Stack, Typography, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material'
import { Delete as DeleteIcon, CheckCircle as ApproveIcon, Cancel as RejectIcon, Refresh as RefreshIcon } from '@mui/icons-material'
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config'
import { buildApiUrl } from '../lib/apiBase'
import { useToast } from '../hooks/useToast'
import { useToastMessages } from '../utils/toastMessages'
import { VideoSuggestion } from '../types/video'

// Legacy export for backward compatibility
export type Suggestion = VideoSuggestion

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers);
    const credentials = options.credentials || 'include';
    maybeAttachAuthorization(headers, credentials as RequestCredentials);
    const res = await fetch(url, { ...options, headers, credentials });
    return res;
}

const AdminSuggestions: React.FC = () => {
    const { toastPromise, showError } = useToast()
    const toastMessages = useToastMessages()
    const [rows, setRows] = React.useState<VideoSuggestion[]>([])
    const [loading, setLoading] = React.useState(true)
    const [loadError, setLoadError] = React.useState(false)
    const [pending, setPending] = React.useState(false)
    const mutationPending = React.useRef(false)
    const [confirm, setConfirm] = React.useState<{ open: boolean, action: 'approve' | 'reject' | 'delete' | null, id: number | null, title?: string }>({ open: false, action: null, id: null })

    const load = async () => {
        setLoading(true)
        setLoadError(false)
        try {
            const res = await fetchWithAuth(buildApiUrl('/video-suggestions'))
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const data = await res.json()
            setRows(Array.isArray(data) ? data : [])
        } catch (e: any) {
            setLoadError(true)
            showError(e?.message || toastMessages.seriesLoadError())
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => { load() }, [])

    const updateStatus = async (id: number, status: 'APPROVED' | 'REJECTED') => {
        const promise = fetchWithAuth(buildApiUrl(`/video-suggestions/${id}/status`), {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status })
        }).then(async (res) => {
            if (!res.ok) throw new Error(await res.text() || `Fehler (${res.status})`)
            return res
        })

        return toastPromise(promise, {
            loading: 'Aktualisiere Status...',
            success: () => {
                load()
                return status === 'APPROVED' ? toastMessages.suggestionApproved() : toastMessages.suggestionRejected()
            },
            error: (e: any) => {
                return e?.message || toastMessages.suggestionError()
            },
        }).catch(() => {
            // The toast already displays the error; keep the existing rows.
        })
    }

    const remove = async (id: number) => {
        const promise = fetchWithAuth(buildApiUrl(`/video-suggestions/${id}`), { method: 'DELETE' }).then(async (res) => {
            if (!res.ok) throw new Error(await res.text() || `Fehler (${res.status})`)
            return res
        })

        return toastPromise(promise, {
            loading: 'Lösche...',
            success: () => {
                setRows(prev => prev.filter(r => r.id !== id))
                return toastMessages.suggestionDeleted()
            },
            error: (e: any) => {
                return e?.message || toastMessages.suggestionError()
            },
        }).catch(() => {
            // The toast already displays the error; keep the existing rows.
        })
    }

    const onConfirm = async () => {
        if (confirm.id === null || !confirm.action || mutationPending.current) return
        mutationPending.current = true
        setPending(true)
        const id = confirm.id
        const action = confirm.action
        setConfirm({ open: false, action: null, id: null })
        try {
            if (action === 'approve') await updateStatus(id, 'APPROVED')
            if (action === 'reject') await updateStatus(id, 'REJECTED')
            if (action === 'delete') await remove(id)
        } finally {
            mutationPending.current = false
            setPending(false)
        }
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h4">Video Suggestions</Typography>
                <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || pending}>Refresh</Button>
            </Stack>
            {loading && <Typography role="status">Vorschläge werden geladen…</Typography>}
            {loadError && <Alert severity="error">Vorschläge konnten nicht geladen werden. Bitte erneut versuchen.</Alert>}
            <Paper elevation={3}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Titel</TableCell>
                            <TableCell>Serie</TableCell>
                            <TableCell>Reihenfolge</TableCell>
                            <TableCell>Tags</TableCell>
                            <TableCell>URL</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Veröffentlicht</TableCell>
                            <TableCell>Erstellt</TableCell>
                            <TableCell align="right">Aktionen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => (
                            <TableRow key={r.id} hover>
                                <TableCell>{r.title}</TableCell>
                                <TableCell>
                                    {r.series ? (
                                        <Chip
                                            size="small"
                                            label={r.series.title}
                                            variant="outlined"
                                            color="primary"
                                        />
                                    ) : '-'}
                                </TableCell>
                                <TableCell>
                                    {r.orderIndex !== undefined ? r.orderIndex : '-'}
                                </TableCell>
                                <TableCell>{r.tags || '-'}</TableCell>
                                <TableCell>
                                    <a href={r.youtubeUrl} target="_blank" rel="noreferrer">Link</a>
                                </TableCell>
                                <TableCell>
                                    {r.status === 'APPROVED' && <Chip size="small" color="success" label="APPROVED" />}
                                    {r.status === 'PENDING' && <Chip size="small" color="warning" label="PENDING" />}
                                    {r.status === 'REJECTED' && <Chip size="small" color="default" label="REJECTED" />}
                                </TableCell>
                                <TableCell>
                                    {r.publishedAt ? new Date(r.publishedAt).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</TableCell>
                                <TableCell align="right">
                                    <IconButton title="Approve" onClick={() => setConfirm({ open: true, action: 'approve', id: r.id, title: r.title })} disabled={loading || pending || r.status === 'APPROVED'}>
                                        <ApproveIcon />
                                    </IconButton>
                                    <IconButton title="Reject" onClick={() => setConfirm({ open: true, action: 'reject', id: r.id, title: r.title })} disabled={loading || pending || r.status === 'REJECTED'}>
                                        <RejectIcon />
                                    </IconButton>
                                    <IconButton disabled={loading || pending} title="Delete" onClick={() => setConfirm({ open: true, action: 'delete', id: r.id, title: r.title })}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && !loadError && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">Keine Einträge</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, action: null, id: null })} maxWidth="xs" fullWidth>
                <DialogTitle>Bestätigen</DialogTitle>
                <DialogContent>
                    Möchtest du "{confirm.title}" wirklich {confirm.action === 'delete' ? 'löschen' : confirm.action === 'approve' ? 'freigeben' : 'ablehnen'}?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm({ open: false, action: null, id: null })}>Abbrechen</Button>
                    <Button variant="contained" onClick={onConfirm}>OK</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default AdminSuggestions